
import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Calendar, momentLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/ar";
import Select from "react-select";
import {
  CalendarDays,
  CloudRain,
  Clock,
  Droplet,
  List,
  Search,
  SlidersHorizontal,
  Wheat,
  X,
} from "lucide-react";
import api from "../api/api";
import {
  getAgriculturalCategory,
  getEventDurationLabel,
  getEventTitle,
  getEventColors,
  getRainAmountLabel,
  isWaterEvent,
} from "../utils/agriculturalEvents";
import { calendarRangeForAgriculturalEvent } from "../utils/agriculturalDateTime";
import "./Calendar.css";
import RainSourceBadge from "../components/calendar/RainSourceBadge";
import AgriculturalEventDrawer from "../components/calendar/AgriculturalEventDrawer";

const EVENT_FILTER_META = {
  rain: { color: "#87ceeb", labelKey: "rain", fallback: "Rain" },
  manual: { color: "#26a6b7", labelKey: "manual_event", fallback: "Manual" },
  refused: { color: "#b83d53", labelKey: "refused", fallback: "Refused" },
  validated: { color: "#137a56", labelKey: "validated", fallback: "Validated" },
  waiting: { color: "#191970", labelKey: "waiting", fallback: "Waiting" },
};

const MyCalendar = () => {
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [allCalcul, setAllCalcul] = useState([]);
  const [fields, setFields] = useState([]);
  const [selectedFields, setSelectedFields] = useState([]);
  const [showAllEvents, setShowAllEvents] = useState(true);
  const [viewMode, setViewMode] = useState("calendar");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editingRain, setEditingRain] = useState(null);
  const [rainChanged, setRainChanged] = useState(false);
  const [eventFilters, setEventFilters] = useState({
    rain: true,
    manual: true,
    refused: true,
    validated: true,
    waiting: true,
  });
  const [fieldEvents, setFieldEvents] = useState([]);

  useEffect(() => {
    const locale = i18n.language === "en" ? "en-gb" : i18n.language;
    moment.locale(locale);
  }, [i18n.language]);

  const localizer = useMemo(() => momentLocalizer(moment), [i18n.language]);
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  const calendarMessages = (t) => ({
    allDay: t("calendar_allDay") || "All day",
    previous: t("calendar_previous") || "Previous",
    next: t("calendar_next") || "Next",
    today: t("calendar_today") || "Today",
    month: t("calendar_month") || "Month",
    week: t("calendar_week") || "Week",
    day: t("calendar_day") || "Day",
    date: t("calendar_date") || "Date",
    time: t("calendar_time") || "Time",
    event: t("calendar_event") || "Event",
    more: t("calendar_more") || "more",
  });

  const getFieldName = (fieldId) => {
    const field = fields.find((f) => f.Id === fieldId);
    return field ? field.title : t("unknown_field", { defaultValue: "Unknown Field" });
  };

  const getEventsForFields = async (fieldIds) => {
    try {
      const allFieldEvents = [];

      for (const fieldId of fieldIds) {
        const field = fields.find((f) => f.Id === fieldId);
        if (!field) continue;

        const response = await api.get(`/field/get-event/${field.Uid}`);
        const eventData = response.data.events;

        const normalized = eventData.map((ev) => {
          const range = calendarRangeForAgriculturalEvent(ev);
          return {
            ...ev,
            raw_start: ev.start,
            raw_end: ev.end,
            id: ev.id || ev._id,
            start: range.start,
            end: range.end,
            allDay: range.isAllDay,
            time: ev.time ? Number(ev.time) : 0,
            dose: ev.dose ? Number(ev.dose) : 0,
            field_id: fieldId,
          };
        });

        allFieldEvents.push(...normalized);
      }

      setFieldEvents(allFieldEvents);
      return allFieldEvents;
    } catch (error) {
      console.error("Error fetching field events:", error);
      return [];
    }
  };

  const handleFieldSelect = async (selectedOptions) => {
    setSelectedFields(selectedOptions);
    setShowAllEvents(selectedOptions.length === 0);

    if (selectedOptions.length > 0) {
      const fieldIds = selectedOptions.map((opt) => opt.value);
      await getEventsForFields(fieldIds);
    } else {
      setFieldEvents([]);
    }
  };

  const toggleEventFilter = (filterType) => {
    setEventFilters((prev) => ({
      ...prev,
      [filterType]: !prev[filterType],
    }));
  };

  const toHoursAndMinutes = (totalMinutes) => {
    const minutes = totalMinutes % 60;
    const hours = Math.floor(totalMinutes / 60);
    return `${hours}h ${parseFloat(minutes).toFixed(0)}m`;
  };

  useEffect(() => {
    const getAllCalcul = async () => {
      try {
        const response = await api.get("/calcul/all-calcul");
        setAllCalcul(response.data.calcul);
      } catch (error) {
        console.error(error);
      }
    };

    const getDataFields = async () => {
      try {
        const response = await api.get("/field/fields");
        const newData = response.data.farms;
        let Fields = [];
        newData.forEach((item) => {
          const fieldsArr = item.fields || [];
          fieldsArr.forEach((field) => {
            Fields.push({
              title: field.name,
              status: field.status,
              description: field.description,
              Uid: field.uid,
              farm_id: field.farm_id,
              Latitude: field.Latitude,
              Longitude: field.Longitude,
              Id: field.id,
            });
          });
        });
        setFields(Fields);
      } catch (error) {
        console.error(error);
      }
    };

    getAllCalcul();
    getDataFields();
  }, []);

  useEffect(() => {
    let data = [];
    allCalcul.forEach((calcul) => {
      const startDate = new Date(calcul.start_date).toISOString().slice(0, 10);
      const endDate = new Date(calcul.end_date).toISOString().slice(0, 10);
      const resultCalcul = calcul.result || [];

      const shouldShowField =
        showAllEvents || selectedFields.some((f) => f.value === calcul.field_id);

      if (!shouldShowField) {
        return;
      }

      const filteredEvents = resultCalcul.filter((result) => {
        const resultDate = new Date(result.date).toISOString().slice(0, 10);
        return (
          result.irrigationNbr === 1 &&
          resultDate >= startDate &&
          resultDate < endDate
        );
      });

      filteredEvents.forEach((evt) => {
        data.push({
          title: `💧 ${parseFloat(evt.irrigation).toFixed(2)}mm • ⏱️ ${toHoursAndMinutes(evt.irrigationTime)}`,
          allDay: true,
          start: new Date(evt.date),
          end: new Date(evt.date),
          source: "resultCalcul",
          status: evt.status || "validated",
          dose: evt.irrigation,
          time: evt.irrigationTime,
          rain: evt.rain,
          field_id: calcul.field_id,
          category_code: "irrigation",
        });
      });
    });

    if (fieldEvents.length > 0) {
      fieldEvents.forEach((evt) => {
        const source = evt.source || "manual";
        const status = evt.status || "validated";

        data.push({
          ...evt,
          title: getEventTitle(evt, t),
          allDay: evt.allDay !== undefined ? evt.allDay : true,
          start: evt.start,
          end: evt.end,
          source: source,
          status: status,
          dose: evt.dose || 0,
          time: evt.time || 0,
          rain: evt.rain || 0,
          field_id: evt.field_id,
        });
      });
    }

    setEvents(data);
    setIsLoading(false);
  }, [allCalcul, selectedFields, showAllEvents, fieldEvents, t]);

  const getFilteredEvents = () => {
    return events.filter((event) => {
      if (event.source === "rain" && !eventFilters.rain) return false;
      if (event.source === "manual" && !eventFilters.manual) return false;
      if (event.status === "refused" && !eventFilters.refused) return false;
      if (event.status === "validated" && !eventFilters.validated) return false;
      if (event.status === "waiting" && !eventFilters.waiting) return false;
      return true;
    });
  };

  const getFilteredAndSearchedEvents = () => {
    let filtered = getFilteredEvents();

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((event) => {
        const title = typeof event.title === "string" ? event.title.toLowerCase() : "";
        const dose = event.dose?.toString() || "";
        const time = event.time?.toString() || "";
        const fieldName = getFieldName(event.field_id).toLowerCase();
        return (
          title.includes(query) ||
          dose.includes(query) ||
          time.includes(query) ||
          fieldName.includes(query)
        );
      });
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dateFilter === "today") {
      filtered = filtered.filter((event) => {
        const eventDate = new Date(event.start);
        return eventDate >= today && eventDate < tomorrow;
      });
    } else if (dateFilter === "upcoming") {
      filtered = filtered.filter((event) => new Date(event.start) >= now);
    } else if (dateFilter === "past") {
      filtered = filtered.filter((event) => new Date(event.start) < now);
    } else if (dateFilter === "thisWeek") {
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 7);
      filtered = filtered.filter((event) => {
        const eventDate = new Date(event.start);
        return eventDate >= weekStart && eventDate < weekEnd;
      });
    } else if (dateFilter === "thisMonth") {
      const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
      const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      filtered = filtered.filter((event) => {
        const eventDate = new Date(event.start);
        return eventDate >= monthStart && eventDate < monthEnd;
      });
    }

    return filtered.sort((a, b) => new Date(a.start) - new Date(b.start));
  };

  const eventStyleGetter = (event) => {
    return {
      style: {
        ...getEventColors(event),
        borderRadius: "12px",
        padding: "4px 8px",
        margin: "2px 0",
        fontSize: "12px",
        lineHeight: "1.2em",
        overflow: "hidden",
        boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
        cursor: "pointer",
      },
    };
  };

  const handleEventClick = (event) => {
    if (getAgriculturalCategory(event.category_code, event).code === "rain") {
      setEditingRain(event);
      return;
    }
    setSelectedEvent(event);
  };

  const refreshRainEvents = async () => {
    setRainChanged(true);
    await getEventsForFields(selectedFields.map((field) => field.value));
  };

  const closeModal = () => {
    setSelectedEvent(null);
  };

  useEffect(() => {
    if (!selectedEvent) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeModal();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedEvent]);

  const filteredEvents = getFilteredEvents();
  const filteredAndSearchedEvents = getFilteredAndSearchedEvents();
  const hasActiveListFilters = Boolean(searchQuery) || dateFilter !== "all";

  return (
    <main className="calendar-page">
      <header className="calendar-page-header">
        <div>
          <span className="calendar-page-eyebrow">SMARTFARM</span>
          <h1>{t("irrigations_schedule") || "Irrigations Schedule"}</h1>
          <p>{t("overview") || "Overview"}</p>
        </div>
        <div className="calendar-page-summary">
          <CalendarDays size={18} aria-hidden="true" />
          <div>
            <span>{t("events", { defaultValue: "Events" })}</span>
            <strong aria-live="polite">{isLoading ? "…" : filteredEvents.length}</strong>
          </div>
        </div>
      </header>

      {rainChanged && <div className="calendar-rain-change-notice" role="status">{t("rain_recalculation_pending")}</div>}

      <section className="calendar-field-selector">
        <div className="calendar-field-selector-copy">
          <span className="calendar-field-icon">
            <SlidersHorizontal size={18} aria-hidden="true" />
          </span>
          <div>
            <span>{t("filter_fields", { defaultValue: "Filter by field" })}</span>
            <strong>{t("All_fields") || "All fields"}</strong>
          </div>
        </div>
        <Select
          className="calendar-fields-select"
          classNamePrefix="calendar-select"
          isMulti
          onChange={handleFieldSelect}
          options={fields.map((field) => ({
            value: field.Id,
            label: field.title,
          }))}
          placeholder={t("All_fields") || "All fields"}
          aria-label={t("filter_fields", { defaultValue: "Filter by field" })}
        />
      </section>

      <section className="calendar-main-card">
        <div dir={dir}>
          <div className="calendar-controls">
            <div className="calendar-controls-top">
              <div className="calendar-view-switch" role="group" aria-label={t("calendar_view") || "Calendar view"}>
                <button
                  type="button"
                  className={viewMode === "calendar" ? "is-active" : ""}
                  onClick={() => setViewMode("calendar")}
                  aria-pressed={viewMode === "calendar"}
                >
                  <CalendarDays size={16} aria-hidden="true" />
                  {t("calendar_view") || "Calendar"}
                </button>
                <button
                  type="button"
                  className={viewMode === "list" ? "is-active" : ""}
                  onClick={() => setViewMode("list")}
                  aria-pressed={viewMode === "list"}
                >
                  <List size={16} aria-hidden="true" />
                  {t("list_view") || "List"}
                </button>
              </div>

              {viewMode === "list" && (
                <div className="calendar-search">
                  <Search className="calendar-search-icon" size={17} aria-hidden="true" />
                  <input
                    type="text"
                    id="calendar-search-input"
                    placeholder={t("search_events") || "Search events or fields..."}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    aria-label={t("search_events") || "Search events or fields..."}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      className="calendar-search-clear"
                      aria-label={t("clear_search", { defaultValue: "Clear search" })}
                      onClick={() => setSearchQuery("")}
                    >
                      <X size={17} aria-hidden="true" />
                    </button>
                  )}
                </div>
              )}
            </div>

            {viewMode === "list" && (
              <div className="calendar-date-filters">
                <span>{t("filter_by_date") || "Filter"}:</span>
                {[
                  { value: "all", label: t("all_dates") || "All" },
                  { value: "today", label: t("today") || "Today" },
                  { value: "thisWeek", label: t("this_week") || "This Week" },
                  { value: "thisMonth", label: t("this_month") || "This Month" },
                  { value: "upcoming", label: t("upcoming") || "Upcoming" },
                  { value: "past", label: t("past") || "Past" },
                ].map((filter) => (
                  <button
                    type="button"
                    className={`calendar-filter-chip ${dateFilter === filter.value ? "is-active" : ""}`}
                    key={filter.value}
                    onClick={() => setDateFilter(filter.value)}
                    aria-pressed={dateFilter === filter.value}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            )}

            {viewMode === "list" && hasActiveListFilters && (
              <div className="calendar-filter-summary">
                <span>
                  {t("showing") || "Showing"} {filteredAndSearchedEvents.length}{" "}
                  {t("events") || "events"}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery("");
                    setDateFilter("all");
                  }}
                >
                  {t("clear_filters") || "Clear"}
                </button>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="calendar-loading-state" role="status" aria-live="polite" aria-busy="true">
              <span className="calendar-loading-spinner" aria-hidden="true" />
              <p>{t("loading") || "Loading..."}</p>
            </div>
          ) : viewMode === "calendar" ? (
            <div className="calendar-calendar-view">
              <Calendar
                localizer={localizer}
                culture={i18n.language === "ar" ? "ar" : "en-GB"}
                views={["month", "week", "day"]}
                events={filteredEvents}
                startAccessor="start"
                endAccessor="end"
                messages={calendarMessages(t)}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleEventClick}
                components={{ event: ({ event }) => (
                  <div>
                    <div>{getEventTitle(event, t)}</div>
                    <RainSourceBadge event={event} t={t} />
                    {getAgriculturalCategory(event.category_code, event).code === "rain" && (
                      <div>{getRainAmountLabel(event, t)}: {Number(event.dose || 0).toFixed(2)} mm</div>
                    )}
                  </div>
                ) }}
                style={{ height: "100%" }}
              />
            </div>
          ) : (
            <div className="calendar-list-view">
              {filteredAndSearchedEvents.length === 0 ? (
                <div className="calendar-empty-state">
                  <div className="calendar-empty-icon" aria-hidden="true"><Search size={28} /></div>
                  <h5>{t("no_events_found") || "No events found"}</h5>
                  <p className="calendar-empty-hint">
                    {hasActiveListFilters
                      ? t("try_different_filters") || "Try different filters"
                      : t("no_events") || "No events available"}
                  </p>
                </div>
              ) : (
                <div className="calendar-event-list">
                  {filteredAndSearchedEvents.map((event, index) => {
                    const eventColor = eventStyleGetter(event).style.backgroundColor;
                    const isToday =
                      new Date(event.start).toDateString() === new Date().toDateString();
                    const fieldName = getFieldName(event.field_id);

                    return (
                      <div
                        className={`calendar-event-card${isToday ? " is-today" : ""}`}
                        key={index}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleEventClick(event)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleEventClick(event);
                          }
                        }}
                        style={{ "--event-accent": eventColor }}
                      >
                        {isToday && (
                          <div className="calendar-event-today-badge">
                            {t("today") || "Today"}
                          </div>
                        )}

                        <div className="calendar-event-field-badge">
                          <Wheat size={13} aria-hidden="true" /> {fieldName}
                        </div>

                        <div className="calendar-event-card-title">
                          <div className="calendar-event-dot" style={{ backgroundColor: eventColor }}></div>
                          <h6>
                            {getEventTitle(event, t)}
                          </h6>
                        </div>
                        {isWaterEvent(event) && <div>{getRainAmountLabel(event, t)}: {Number(event.dose || 0).toFixed(2)} mm</div>}

                        <div className="calendar-event-card-date">
                          <RainSourceBadge event={event} t={t} />
                          <strong><CalendarDays size={13} aria-hidden="true" /> {t("date") || "Date"}:</strong>{" "}
                          {new Date(event.start).toLocaleDateString(i18n.language, {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          })}
                        </div>

                        <div className="calendar-event-card-meta">
                          <div>
                            <strong><Clock size={13} aria-hidden="true" /> {t("time") || "Duration"}:</strong>{" "}
                            {getEventDurationLabel(event, t)}
                          </div>
                          <div>
                            <strong><CloudRain size={13} aria-hidden="true" /> {t("Rain") || "Rain"}:</strong>{" "}
                            {event.rain ? parseFloat(event.rain).toFixed(2) : "0.00"} mm
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="calendar-event-legend">
            <h6>{t("event_types") || "Event Types"}</h6>
            <div className="calendar-legend-filters">
              {Object.entries(EVENT_FILTER_META).map(([key, meta]) => (
                <button
                  type="button"
                  className={`calendar-legend-chip ${eventFilters[key] ? "is-active" : ""}`}
                  aria-pressed={eventFilters[key]}
                  key={key}
                  onClick={() => toggleEventFilter(key)}
                  style={{ "--chip-color": meta.color }}
                >
                  <div className="calendar-legend-dot" style={{ backgroundColor: meta.color }}></div>
                  <span>{t(meta.labelKey) || meta.fallback}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {selectedEvent && (
        <div
          className="calendar-modal-backdrop"
          onClick={closeModal}
          role="presentation"
        >
          <div
            className="calendar-event-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t("event_details") || "Event Details"}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="calendar-modal-header">
              <div className="calendar-modal-header-top">
                <h4>{t("event_details") || "Event Details"}</h4>
                <button
                  type="button"
                  className="calendar-modal-close"
                  aria-label={t("close") || "Close"}
                  onClick={closeModal}
                >
                  <X size={20} aria-hidden="true" />
                </button>
              </div>
              <div className="calendar-event-field-badge">
                <Wheat size={13} aria-hidden="true" /> {getFieldName(selectedEvent.field_id)}
              </div>
            </div>

            <div className="calendar-modal-body">
              <div className="calendar-modal-grid">
                {isWaterEvent(selectedEvent) ? (
                  <div
                    className="calendar-modal-stat"
                    style={{ "--stat-accent": eventStyleGetter(selectedEvent).style.backgroundColor }}
                  >
                    <div className="calendar-modal-stat-label">
                      <Droplet size={14} aria-hidden="true" /> {t("Irrigation_Dose", { defaultValue: "IRRIGATION DOSE" })}
                    </div>
                    <div className="calendar-modal-stat-value is-large">
                      {selectedEvent.dose ? parseFloat(selectedEvent.dose).toFixed(2) : "0.00"} mm
                    </div>
                  </div>
                ) : (
                  <div
                    className="calendar-modal-stat"
                    style={{ "--stat-accent": eventStyleGetter(selectedEvent).style.backgroundColor }}
                  >
                    <div className="calendar-modal-stat-label">
                      {t(getAgriculturalCategory(selectedEvent.category_code, selectedEvent).labelKey)}
                    </div>
                    <div className="calendar-modal-stat-value">
                      {getEventTitle(selectedEvent, t)}
                    </div>
                    {selectedEvent.description && (
                      <p className="calendar-modal-stat-description">{selectedEvent.description}</p>
                    )}
                  </div>
                )}

                <div className="calendar-modal-stat is-info">
                  <div className="calendar-modal-stat-label">
                    <CalendarDays size={14} aria-hidden="true" /> {t("date") || "DATE"}
                  </div>
                  <div className="calendar-modal-stat-value is-medium">
                    {new Date(selectedEvent.start).toLocaleDateString(i18n.language, {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                </div>

                <div className="calendar-modal-split">
                  <div className="calendar-modal-stat is-warning is-centered">
                    <div className="calendar-modal-stat-label">
                      <Clock size={14} aria-hidden="true" /> {t("time") || "DURATION"}
                    </div>
                    <div className="calendar-modal-stat-value is-medium">
                      {getEventDurationLabel(selectedEvent, t)}
                    </div>
                  </div>

                  <div className="calendar-modal-stat is-info is-centered">
                    <div className="calendar-modal-stat-label">
                      <CloudRain size={14} aria-hidden="true" /> {t("Rain") || "RAIN"}
                    </div>
                    <div className="calendar-modal-stat-value is-medium">
                      {selectedEvent.rain ? parseFloat(selectedEvent.rain).toFixed(2) : "0.00"} mm
                    </div>
                  </div>
                </div>

                <div className="calendar-modal-stat">
                  <div className="calendar-modal-stat-label">
                    📊 {t("status") || "STATUS"}
                  </div>
                  <RainSourceBadge event={selectedEvent} t={t} />
                  <div
                    className="calendar-modal-status-pill"
                    style={{ backgroundColor: eventStyleGetter(selectedEvent).style.backgroundColor }}
                  >
                    <span className="calendar-modal-status-dot"></span>
                    {selectedEvent.source === "rain" && (t("rain") || "Rain")}
                    {selectedEvent.source === "manual" && (t("manual_event") || "Manual")}
                    {selectedEvent.source === "resultCalcul" &&
                      selectedEvent.status === "validated" &&
                      (t("validated") || "Validated")}
                    {selectedEvent.source === "resultCalcul" &&
                      selectedEvent.status === "refused" &&
                      (t("refused") || "Refused")}
                    {selectedEvent.source === "resultCalcul" &&
                      selectedEvent.status === "waiting" &&
                      (t("waiting") || "Waiting")}
                  </div>
                </div>
              </div>
            </div>

            <div className="calendar-modal-footer">
              <button type="button" className="calendar-modal-action" onClick={closeModal}>
                {t("close") || "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
      <AgriculturalEventDrawer
        show={Boolean(editingRain)}
        initialEvent={editingRain}
        t={t}
        dir={dir}
        onHide={() => setEditingRain(null)}
        onSaved={refreshRainEvents}
        onDeleted={refreshRainEvents}
      />
    </main>
  );
};

export default MyCalendar;
